<script setup lang="ts">
import type { CatalogTemplate } from '~/store/billing'
import type { OsSection, TemplateShortfall, TemplateSpecs } from '~/utils/template-catalog'

/**
 * Operating systems: one tile per distribution, and the release chosen in the
 * body of the chosen tile.
 *
 * Tiles sit in sections by `os_family` — Windows, Linux, the rest — because the
 * alphabet put Windows Server, ~68% of the fleet's installed systems, on the
 * tenth tile of ten, and Windows is what most customers order. Inside a section
 * Windows Server leads Windows and Ubuntu and Debian lead Linux
 * (`compareOsFamilies`, the same order as the website).
 *
 * The release used to be a panel under the whole grid: a customer who clicked
 * Ubuntu, top left, found its versions below FreeBSD — on a phone, screens
 * away. It is asked where the distribution was, the way DigitalOcean, Hetzner
 * and Vultr ask it. What the chosen release MEANS — its support window, its end
 * of life, a plan that cannot run it — is said by the card at the top of the
 * step (`TemplatePickerSelected`), which says it for apps too.
 */
const props = defineProps<{
  templates: CatalogTemplate[]
  modelValue: string | null
  specs: TemplateSpecs
  /** The release chosen before in each distribution (category → code), kept by the shell. */
  remembered?: Record<string, string>
}>()

const emit = defineEmits<{ 'update:modelValue': [value: string] }>()

const { t, locale } = useI18n()
const selectedCategory = ref<string | null>(null)

type Family = { category: string; section: OsSection; versions: CatalogTemplate[] }

const families = computed<Family[]>(() => {
  const groups = new Map<string, CatalogTemplate[]>()
  // The catalog already lists versions newest first within a category.
  for (const template of props.templates) {
    groups.set(template.category, [...(groups.get(template.category) ?? []), template])
  }
  return [...groups.entries()]
    .map(([category, versions]) => ({ category, section: osSection(versions[0]!), versions }))
    .sort(compareOsFamilies)
})

const sections = computed(() =>
  OS_SECTION_ORDER.map((section) => ({
    section,
    label: t(`billing.page.checkout.os.catalog.sections.${section}`),
    families: families.value.filter((family) => family.section === section),
  })).filter((group) => group.families.length > 0)
)

const activeFamily = computed<Family | null>(() => {
  const holding = families.value.find((family) =>
    family.versions.some((template) => template.code === props.modelValue)
  )
  if (holding) return holding
  return families.value.find((family) => family.category === selectedCategory.value) ?? null
})

watch(
  activeFamily,
  (family) => {
    if (family) selectedCategory.value = family.category
  },
  { immediate: true }
)

function isActive(family: Family): boolean {
  return activeFamily.value?.category === family.category
}

/**
 * What a click on the tile chooses: the release chosen there before, else the
 * default. The control lives in the tile now, so a customer who picks Ubuntu
 * 22.04, clicks Debian to compare and comes back expects the tile to still say
 * 22.04 — one that falls back to the default reads as having forgotten.
 */
function familyPick(family: Family): CatalogTemplate | undefined {
  const remembered = props.remembered?.[family.category]
  return family.versions.find((template) => template.code === remembered) ?? defaultVersion(family.versions)
}

function selectFamily(family: Family) {
  selectedCategory.value = family.category
  const already = family.versions.find((template) => template.code === props.modelValue)
  const pick = already ?? familyPick(family)
  if (pick) emit('update:modelValue', pick.code)
}

/**
 * The tile's second line. Not chosen: the release a click would choose, and
 * how many there are ("26.04 LTS · 6 versões"). Chosen, with releases to choose
 * from: the count alone — the control beneath it names the release, and saying
 * it twice in one tile is how the two drift apart.
 */
function tileSubtitle(family: Family): string {
  const count = family.versions.length
  const countLabel = t('billing.page.checkout.os.catalog.versionCount', { count }, count)
  if (count > 1 && isActive(family)) return countLabel
  const pick = familyPick(family)
  const label = pick ? `${pick.version_label || pick.name}${pick.is_lts ? ` ${t('billing.page.checkout.os.lts')}` : ''}` : ''
  return count > 1 ? `${label} · ${countLabel}` : label
}

// ── Keyboard: one tab stop for all tiles ────────────────────────────────────
// The release control sits BESIDE the chosen tile's radio, not inside it, so
// the roving handler (which acts only on `[data-roving-id]`) leaves its arrow
// keys alone, and Tab goes from the chosen tile straight to its release.
const tiles = ref<HTMLElement | null>(null)
const roving = useRovingGrid(tiles, {
  ids: () => sections.value.flatMap((section) => section.families.map((family) => family.category)),
  preferred: () => activeFamily.value?.category ?? null,
})

// ── Version options ────────────────────────────────────────────────────────
// One object per version of the active distribution, carrying everything the
// dropdown row needs: what to call it, how its support window reads, and
// whether the configuration being bought can actually run it.

type VersionOption = {
  code: string
  template: CatalogTemplate
  label: string
  isLts: boolean
  isRecommended: boolean
  supportStatus: 'supported' | 'ending_soon' | 'ended' | null
  eolAt: string | null
  unmet: TemplateShortfall | null
}

const versionOptions = computed<VersionOption[]>(
  () =>
    activeFamily.value?.versions.map((template) => ({
      code: template.code,
      template,
      label: template.version_label || template.name,
      isLts: template.is_lts,
      isRecommended: template.is_recommended,
      supportStatus: template.support_status,
      eolAt: template.eol_at,
      unmet: templateFit(template, props.specs),
    })) ?? []
)

const selectedVersion = computed<VersionOption | null>(
  () => versionOptions.value.find((option) => option.code === props.modelValue) ?? null
)

const selectedVersionCode = computed<string | null>({
  get: () => selectedVersion.value?.code ?? null,
  set: (code) => {
    if (code) emit('update:modelValue', code)
  },
})
</script>

<template>
  <!--
    One GROUP per section, labelled by its heading — not a radiogroup, because
    the chosen tile carries its release control, and a radiogroup may own only
    radios: a combobox inside one is invalid, and assistive technology reads
    it as a broken group. The tiles are still radios and one roving tab stop
    still spans every section: arrows move between tiles without choosing,
    Enter or Space chooses, and Tab goes on to the chosen tile's release.
  -->
  <div ref="tiles" class="space-y-3" @keydown="roving.onKeydown" @focusin="roving.onFocusin">
    <div v-for="group in sections" :key="group.section" class="space-y-1.5">
      <p
        :id="`os-section-${group.section}`"
        class="text-[11px] font-semibold uppercase tracking-wide text-muted"
      >
        {{ group.label }}
      </p>
      <!--
        One column on a phone: two columns of ~160 px cut "Windows Server" to
        "Windows S…" and left the chosen tile no room for its release. Tiles
        start at the top of their row, so the chosen one grows alone.
      -->
      <div
        role="group"
        :aria-labelledby="`os-section-${group.section}`"
        class="grid grid-cols-1 items-start gap-2 @sm:grid-cols-2 @xl:grid-cols-3 @4xl:grid-cols-4"
      >
        <div
          v-for="family in group.families"
          :key="family.category"
          :class="[
            'rounded-xl border transition',
            isActive(family)
              ? 'border-primary bg-primary/5 ring-1 ring-primary on-tint dark:bg-primary/10'
              : 'border-default bg-default hover:border-accented hover:bg-elevated/40',
          ]"
        >
          <button
            type="button"
            role="radio"
            data-testid="os-distro-tile"
            :data-roving-id="family.category"
            :aria-checked="isActive(family)"
            :tabindex="roving.tabStop.value === family.category ? 0 : -1"
            class="flex min-h-16 w-full items-center gap-2.5 rounded-xl p-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            @click="selectFamily(family)"
          >
            <TemplateLogo
              :icon-url="family.versions[0]?.icon_url"
              :fallback="fallbackIcon(family.versions[0]!)"
              size="sm"
            />
            <span class="min-w-0">
              <span data-testid="os-distro-name" class="block truncate text-sm font-semibold">{{ family.category }}</span>
              <!--
                What a click on the tile would choose. Falls back to the full
                name rather than to an empty line: an API old enough to predate
                `version_label` would otherwise blank the subtitle during a
                staged deploy.
              -->
              <span class="block truncate text-[11px] text-muted">{{ tileSubtitle(family) }}</span>
            </span>
          </button>

          <!--
            The release, in the body of the tile that chose the distribution —
            the second half of one question, asked where the first was answered.
            A distribution with one release gets no control: a dropdown holding
            a single option is a click that can only return the same answer.
          -->
          <div
            v-if="isActive(family) && (versionOptions.length > 1 || selectedVersion?.unmet || selectedVersion?.supportStatus === 'ended')"
            class="space-y-1.5 px-2.5 pb-2.5"
          >
            <USelectMenu
              v-if="versionOptions.length > 1"
              v-model="selectedVersionCode"
              data-testid="os-version-select"
              :items="versionOptions"
              value-key="code"
              label-key="label"
              :search-input="versionOptions.length > 6 && {
                placeholder: $t('billing.page.checkout.os.searchVersion'),
              }"
              size="lg"
              class="w-full"
              :content="{ align: 'start' }"
              :ui="{ content: 'min-w-[min(20rem,calc(100vw-2rem))]' }"
              :aria-label="$t('billing.page.checkout.os.chooseVersion', { name: family.category })"
            >
              <template #default>
                <span v-if="selectedVersion" class="flex min-w-0 items-center gap-2 overflow-hidden">
                  <span class="truncate font-medium">{{ selectedVersion.label }}</span>
                  <UBadge v-if="selectedVersion.isLts" color="primary" variant="subtle" size="sm" class="shrink-0">
                    {{ $t('billing.page.checkout.os.lts') }}
                  </UBadge>
                  <UBadge v-if="selectedVersion.isRecommended" color="success" variant="subtle" size="sm" class="shrink-0">
                    {{ $t('billing.page.checkout.os.recommended') }}
                  </UBadge>
                </span>
                <span v-else class="text-muted">
                  {{ $t('billing.page.checkout.os.selectVersion') }}
                </span>
              </template>

              <template #item="{ item }">
                <!--
                  Wraps rather than overflows: on a phone the status words go to
                  a second line instead of pushing the row wider than the list.
                -->
                <span class="flex w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 py-0.5">
                  <span class="truncate font-medium">{{ item.label }}</span>
                  <UBadge v-if="item.isLts" color="primary" variant="subtle" size="sm">
                    {{ $t('billing.page.checkout.os.lts') }}
                  </UBadge>
                  <UBadge v-if="item.isRecommended" color="success" variant="subtle" size="sm">
                    {{ $t('billing.page.checkout.os.recommended') }}
                  </UBadge>
                  <span class="ml-auto flex shrink-0 items-center gap-3 text-[11px]">
                    <!--
                      Status ships as icon + words, never colour alone — the row
                      has to read the same to someone who cannot tell the red
                      chip from the green one.
                    -->
                    <span
                      v-if="item.supportStatus === 'ended'"
                      :class="['inline-flex items-center gap-1', SUPPORT_TONE.ended.class]"
                    >
                      <UIcon :name="SUPPORT_TONE.ended.icon" class="size-3" />
                      {{ $t('billing.page.checkout.os.support.ended') }}
                    </span>
                    <span
                      v-else-if="item.supportStatus === 'ending_soon'"
                      :class="['inline-flex items-center gap-1', SUPPORT_TONE.ending_soon.class]"
                    >
                      <UIcon :name="SUPPORT_TONE.ending_soon.icon" class="size-3" />
                      {{ $t('billing.page.checkout.os.support.endingSoon', { date: eolMonthLabel(item.eolAt, locale) }) }}
                    </span>
                    <span
                      v-if="item.unmet"
                      class="inline-flex items-center gap-1 text-muted"
                    >
                      <UIcon name="i-lucide-cpu" class="size-3" />
                      {{ $t('billing.page.checkout.os.needsSpec', { spec: specLabel(item.unmet) }) }}
                    </span>
                  </span>
                </span>
              </template>
            </USelectMenu>

            <!--
              What this release means, said in the tile too. The full alerts —
              the plan's remedy, the end-of-life explanation — are in the card
              at the top of the step, which on a phone is a screen away from a
              tile low in the list; this is the line that keeps the consequence
              where the choice was made.
            -->
            <p
              v-if="selectedVersion?.unmet || selectedVersion?.supportStatus === 'ended'"
              class="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] leading-4"
            >
              <span
                v-if="selectedVersion.unmet"
                class="inline-flex items-center gap-1 text-warning-800 dark:text-warning-400"
              >
                <UIcon name="i-lucide-alert-triangle" class="size-3 shrink-0" />
                {{ $t('billing.page.checkout.os.needsSpec', { spec: specLabel(selectedVersion.unmet) }) }}
              </span>
              <span
                v-if="selectedVersion.supportStatus === 'ended'"
                :class="['inline-flex items-center gap-1', SUPPORT_TONE.ended.class]"
              >
                <UIcon :name="SUPPORT_TONE.ended.icon" class="size-3 shrink-0" />
                {{ $t('billing.page.checkout.os.support.ended') }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
